/**
 * Work out the type name to file an error under.
 *
 * Every error logger in this codebase used `error.constructor.name`, which is
 * the class's *binding* name — and the server bundle is minified, so on
 * production that binding is a mangled letter. `VendooRpcError` has been filing
 * itself as `J` for as long as the shim has been live: 69 rows a day whose
 * `error_type` column says nothing, cannot be grouped, and cannot be searched.
 *
 * `error.name` is a string on the prototype, so minification cannot touch it,
 * and every custom error class here sets it (`VendooRpcError`, `ZodError`,
 * `PrismaClientKnownRequestError`). It is only useless when a class forgets to
 * set it, in which case it reads 'Error' and the constructor name — if it still
 * looks like a real identifier — is the better answer.
 *
 * No imports, deliberately: this is called from inside the error loggers, which
 * the ClickHouse client imports, and a cycle there breaks all analytics.
 */

/**
 * Terser names mangled bindings from a short alphabet: `J`, `Zn`, `kA`. Real
 * error class names in this codebase are all longer than `Error` itself, so
 * anything under four characters is treated as mangled rather than reported as
 * a type nobody can look up.
 */
const MANGLED_NAME_MAX_LENGTH = 3;

export function errorTypeName(error: unknown, fallback = 'UnknownError'): string {
  if (!(error instanceof Error)) return fallback;

  // Set explicitly by every custom error class worth distinguishing, and immune
  // to minification because it is a string, not an identifier.
  const declared = typeof error.name === 'string' ? error.name.trim() : '';
  if (declared && declared !== 'Error') return declared;

  const constructed = typeof error.constructor?.name === 'string' ? error.constructor.name.trim() : '';
  if (
    constructed &&
    constructed !== 'Error' &&
    constructed !== 'Object' &&
    constructed.length > MANGLED_NAME_MAX_LENGTH
  ) {
    return constructed;
  }

  // 'Error' is imprecise but true. 'J' is neither.
  return 'Error';
}
